import { apolloClient, UPDATE_USER_QUIZZES_MUTATION, UPDATE_USER_QUIZ_MUTATION } from '~/common/apollo'
import { GET_SELECT_OPTIONS, GET_USER_FORM } from '~/common/apollo/queries/user.query'
import { PATHS } from '~/common/constants'
import { action, computed, loading, observable, persist, runInAction, store } from '~/common/mobx.decorator'
import { homeStore } from '~/features/home/home.store'
import { useFormatValues } from '~/features/profile-completion/utils/hooks/useFormHelper'
import { getQuizObject } from '~/features/profile-completion/utils/quiz.helper'
import { useNurseWithNCLEXB, useNurseNonNLCEX, useMedTech } from '~/features/profile-completion/utils/hooks/useFieldBranching'
import { resumeParseStore } from '~/features/profile-completion/resume-parse'
import { authStore, notifyStore, routerStore } from '~/stores'
import candidateStore from '~/stores/candidate.store'
import { QUIZ_STATES, PROFILE_COMPLETION_FIELDS_NAME } from './utils/constants'
import { isEmpty } from 'lodash'
import { PROFILE_STEP_SUBTITLE } from '~/common/constants/common.constant'

// Optional fields không cần validate
const OPTIONAL_FIELDS = [PROFILE_COMPLETION_FIELDS_NAME.WHATSAPP_PHONE, PROFILE_COMPLETION_FIELDS_NAME.VISA_SCREENING_OR_ENGLISH_EXAM_DOCUMENT]

const PERCENTAGE_STEP = 20 // 20% per step

/**
 * Note: have no idea why we need the name but this is required field
 */
const NAME = 'initial_screening'

/**
 * Represents the store for managing profile completion data.
 */
@store({ persist: true, name: 'profile-completion' })
class ProfileCompletionStore {
	/** The translation code */
	@observable pageTitle = 'COMPLETE_YOUR_APPLICATION'

	/**
	 * The completion percentage of the profile.
	 * @type {number}
	 */
	@observable percentage = 0

	/**
	 * Contains all parameters used for submitting the profile completion.
	 * Base parameters are:
	 * - kind: value of PROFILE_KINDS
	 * - percent: the completion percentage
	 * @type {Object}
	 */
	@persist @observable params = {
		kind: null,
		percent: 0,
	}

	/**
	 * The status of the profile completion.
	 * @type {null}
	 */
	@persist @observable status = null

	/**
	 * The previous step of the profile completion.
	 * @type {null}
	 */
	@observable previousStep = null

	/**
	 * The state of the profile completion.
	 * @type {null}
	 */
	@observable state = null

	/**
	 * The quiz of the profile completion.
	 * @type {null}
	 */
	@observable quiz = null

	/**
	 * The user's quiz answers.
	 * @type {Array}
	 */
	@observable userQuizAnswers = []

	/**
	 * The ID of the profile completion.
	 * @type {null}
	 */
	@observable id = null

	@observable userQuizzes = []

	@observable currentQuizQuestions = []
	@observable currentQuizAnswers = []

	@observable currentQuizId = null
	@observable currentUserQuiz = null

	@observable percentageStep = {}

	/**
	 * Map to track answer IDs separately from currentQuizAnswers
	 * This prevents unnecessary re-renders when only syncing IDs from API
	 * Key: quizQuestionId, Value: answerId
	 */
	@observable answerIdMap = new Map()

	/**
	 * Track pending auto-save state (true immediately when field changes, before debounce)
	 * This is separate from loading state which only becomes true when API call starts
	 */
	@observable hasPendingAutoSave = false

	/**
	 * Track number of pending auto-save API calls (counter for concurrent calls)
	 * Used to disable Continue button until ALL auto-save calls complete
	 * This is more reliable than @loading decorator which may not track concurrent calls properly
	 */
	@observable pendingAutoSaveCount = 0

	@computed
	get hasNextPage() {
		return this.selectOptions?.pageInfo?.hasNextPage || false
	}

	@computed
	get endCursor() {
		return this.selectOptions?.pageInfo?.endCursor
	}

	/**
	 * The quiz history.
	 *
	 * @type {Object}
	 * @property {Object} previous - The previous quiz's name.
	 * @property {Object} next - The next quiz's name.
	 * @property {Object} current - The current quiz's name.
	 * @property {Object} latest - The latest quiz's name that the user must be completed
	 */
	@observable quizHistory = {
		previous: null,
		current: null,
		next: null,
		latest: null,
	}

	/**
	 * @param {string} key - The key is the value of `PROFILE_COMPLETION_FIELDS_NAME`
	 * @param {Array | Object} value - The value of the answer
	 */
	@observable selectOptionsByDataType = {}

	/**
	 * Accept term and policy
	 * @deprecated will remove soon
	 * */
	@observable acceptTermAndPolicy = false

	/**
	 * Condition to display the checkbox term and policy
	 * @deprecated will remove soon
	 *  */
	@computed
	get isDisplayTermAndPolicy() {
		// TODO: Logic to this play the term and policy
		// if the quiz is the first quiz, we need to display the term and policy
		return !this.currentUserQuiz?.previousStep || this.currentUserQuiz?.previousStep === 'switch_general_information_quiz'
	}

	@computed
	get currentQuizName() {
		return this.currentUserQuiz?.quiz?.name || ''
	}

	@computed
	get isLastQuiz() {
		if (!this.userQuizzes?.length) return false

		// Get last quiz from userQuizzes array
		const lastQuiz = this.userQuizzes[this.userQuizzes.length - 1]

		// Check if current quiz matches last quiz by id
		return (
			this.currentQuizId === lastQuiz.id ||
			// Or check by quiz name
			this.currentQuizName === lastQuiz.quiz?.name
		)
	}

	// Return the total questions of the current quiz to calculate the form complete or further
	@computed
	get currentQuizTotalQuestions() {
		return this.currentUserQuiz?.quiz?.quizQuestions?.length || 0
	}

	@computed
	get totalPercentageCompleted() {
		if (!this.currentUserQuiz?.quiz?.stepPercentage) return 0
		return this.currentUserQuiz.quiz?.stepPercentage
	}

	@computed
	get isCompleted() {
		return this.currentUserQuiz?.quiz?.isCompletedStep || false
	}

	@computed
	get isFirstStep() {
		return isEmpty(this.currentUserQuiz?.previousStep)
	}

	@computed
	get subTitle() {
		if (this.isFirstStep) {
			return 'BACKGROUND'
		}

		const subTitle = PROFILE_STEP_SUBTITLE[this.currentUserQuiz?.quiz?.name]
		if (subTitle) {
			return subTitle
		}

		return null
	}

	@action setQuizId = (quizId) => {
		this.currentQuizId = quizId
	}

	@computed
	get unansweredQuestions() {
		const quizQuestions = this.currentUserQuiz?.quiz?.quizQuestions || []
		const userQuizAnswers = this.currentQuizAnswers || []

		// Create a set of answered question IDs for faster lookup
		const answeredQuestionIds = new Set(userQuizAnswers.filter((x) => !!x.answer).map((answer) => answer.quizQuestionId))

		// Filter out questions that don't have answers
		return quizQuestions.filter((question) => !answeredQuestionIds.has(question.id))
	}

	/**
	 * Map answers theo question name để sử dụng cho branching logic
	 * @returns {Object} { questionName: answerValue }
	 */
	getAnswersByQuestionName = () => {
		const questions = this.currentQuizQuestions || []
		const answers = this.currentQuizAnswers || []

		return questions.reduce((acc, question) => {
			const answer = answers.find((a) => a.quizQuestionId === question.id)
			if (answer) {
				acc[question.name] = answer.answer
			}
			return acc
		}, {})
	}

	/**
	 * Kiểm tra question có nên được hiển thị không (branching logic)
	 * @param {string} questionName
	 * @returns {boolean}
	 */
	shouldShowQuestion = (questionName) => {
		const values = this.getAnswersByQuestionName()
		const quizName = this.currentQuizName

		if (quizName?.includes('_without_nclex')) {
			return useNurseNonNLCEX(questionName, values)
		}

		if (quizName?.includes('nurse_')) {
			return useNurseWithNCLEXB(questionName, values)
		}

		if (quizName?.includes('medical_technologist_')) {
			return useMedTech(questionName, values)
		}

		return true
	}

	/**
	 * Kiểm tra question có phải optional không
	 * @param {string} questionName
	 * @returns {boolean}
	 */
	isOptionalQuestion = (questionName) => {
		return OPTIONAL_FIELDS.includes(questionName)
	}

	/**
	 * Validate xem current quiz đã được hoàn thành chưa
	 * Dựa trên currentQuizAnswers và branching logic (không query DOM)
	 *
	 * @returns {boolean} True nếu tất cả required questions đã có valid answer
	 */
	validateCurrentQuizCompletion = () => {
		const questions = this.currentQuizQuestions || []
		const answers = this.currentQuizAnswers || []

		if (!questions.length) return false

		// Lọc ra những questions visible (theo branching) và required (không optional)
		const visibleRequiredQuestions = questions.filter((question) => {
			const isVisible = this.shouldShowQuestion(question.name)
			const isOptional = this.isOptionalQuestion(question.name)
			return isVisible && !isOptional
		})

		if (!visibleRequiredQuestions.length) return true // Không có required questions

		// Kiểm tra tất cả required questions đã có valid answer trong store
		return visibleRequiredQuestions.every((question) => {
			const answer = answers.find((a) => a.quizQuestionId === question.id)
			if (!answer) return false

			// Kiểm tra có valid text answer
			const hasValidAnswer = answer.answer && answer.answer !== '' && answer.answer !== 'AVOID_ANSWER_BLANK'

			// Kiểm tra có valid files (cho file upload questions)
			const hasValidFiles =
				answer.files &&
				answer.files.length > 0 &&
				answer.files.some((f) => {
					const hasApiFile = f?.file?.fileUrl || f?.file?.blobId
					const hasUploadFile = f?.fileSignedBlobId
					return (hasApiFile || hasUploadFile) && !f?._destroy
				})

			return hasValidAnswer || hasValidFiles
		})
	}

	/**
	 * Get answers from formik values in { questionName: answerValue } format
	 * @param {Object} formikValues - Formik values object { questionId: { answer, name, ... } }
	 * @returns {Object} { questionName: answerValue }
	 */
	getAnswersFromFormikValues = (formikValues) => {
		if (!formikValues) return {}

		return Object.entries(formikValues).reduce((acc, [, value]) => {
			if (value?.name && value?.answer !== undefined) {
				acc[value.name] = value.answer
			}
			return acc
		}, {})
	}

	/**
	 * Check if question should be shown based on formik values (real-time)
	 * @param {string} questionName
	 * @param {Object} formikValues - Formik values
	 * @returns {boolean}
	 */
	shouldShowQuestionWithFormikValues = (questionName, formikValues) => {
		const values = this.getAnswersFromFormikValues(formikValues)
		const quizName = this.currentQuizName

		if (quizName?.includes('_without_nclex')) {
			return useNurseNonNLCEX(questionName, values)
		}

		if (quizName?.includes('nurse_')) {
			return useNurseWithNCLEXB(questionName, values)
		}

		if (quizName?.includes('medical_technologist_')) {
			return useMedTech(questionName, values)
		}

		return true
	}

	/**
	 * Validate quiz completion using Formik values (real-time form state)
	 * This is more accurate than validateCurrentQuizCompletion because it uses
	 * the actual form values instead of store values (which may be stale due to debounce)
	 *
	 * @param {Object} formikValues - Formik values object { questionId: { answer, name, files, ... } }
	 * @returns {boolean} True if all visible required questions have valid answers
	 */
	validateWithFormikValues = (formikValues) => {
		const questions = this.currentQuizQuestions || []

		if (!questions.length) return false
		if (!formikValues) return false

		// Filter visible and required questions using Formik values for branching
		const visibleRequiredQuestions = questions.filter((question) => {
			const isVisible = this.shouldShowQuestionWithFormikValues(question.name, formikValues)
			const isOptional = this.isOptionalQuestion(question.name)
			return isVisible && !isOptional
		})

		if (!visibleRequiredQuestions.length) return true

		// Check all required questions have valid answers in Formik values
		const invalidQuestions = []
		const result = visibleRequiredQuestions.every((question) => {
			const formikValue = formikValues[question.id]
			if (!formikValue) {
				invalidQuestions.push({ name: question.name, reason: 'no formikValue' })
				return false
			}

			// For checkbox questions (like accept terms), answer must be 'true' (checked)
			if (question.questionType === 'checkbox') {
				const isValid = formikValue.answer === 'true'
				if (!isValid) invalidQuestions.push({ name: question.name, type: 'checkbox', answer: formikValue.answer })
				return isValid
			}

			// Check for valid text answer
			const hasValidAnswer = formikValue.answer && formikValue.answer !== '' && formikValue.answer !== 'AVOID_ANSWER_BLANK'

			// Check for valid files (for file upload questions)
			// Filter out destroyed files first, then check if any valid file exists
			const activeFiles = formikValue.files?.filter((f) => !f?._destroy) || []
			const hasValidFiles =
				activeFiles.length > 0 &&
				activeFiles.some((f) => {
					// File from API response (nested structure: { file: { fileUrl, blobId } })
					const hasNestedApiFile = f?.file?.fileUrl || f?.file?.blobId
					// File from API response (flat structure: { id, blobId })
					const hasFlatApiFile = f?.blobId || f?.id
					// Newly uploaded file (has signedBlobId from direct upload)
					const hasUploadFile = f?.fileSignedBlobId || f?.signedBlobId
					// File object from dropzone (has name, size, etc.)
					const hasLocalFile = f?.name && f?.size
					return hasNestedApiFile || hasFlatApiFile || hasUploadFile || hasLocalFile
				})

			const isValid = hasValidAnswer || hasValidFiles
			if (!isValid) {
				invalidQuestions.push({
					name: question.name,
					type: question.questionType,
					answer: formikValue.answer,
					allFiles: formikValue.files,
					activeFiles: activeFiles,
				})
			}
			return isValid
		})

		return result
	}

	@action setCurrentUserQuiz = (userQuiz) => {
		this.quizHistory = {
			latest: userQuiz?.quiz?.name,
			previous: userQuiz?.previousStep,
			current: userQuiz?.quiz?.name,
			next: null,
		}
		this.setQuizId(userQuiz?.quiz?.id) // Use quiz.id for API calls
		this.currentUserQuiz = userQuiz
	}

	@action
	addResumeParseAnswer = (answers) => {
		this.currentQuizAnswers = [...this.currentQuizAnswers, ...answers]

		// Auto-save resume parse answers to server
		// Each answer is submitted individually to prevent race conditions
		answers.forEach((answer) => {
			this.submitSingleAnswer(answer)
		})
	}

	/**
	 *
	 * @param {*} newQuiz - previous quiz
	 * @param {*} currentQuiz - the current quiz
	 */
	@action handleBackQuiz = (newQuiz, currentQuiz) => {
		this.quizHistory = {
			...this.quizHistory,
			previous: newQuiz?.previousStep,
			current: newQuiz?.quiz?.name,
			next: currentQuiz?.quiz?.name,
		}
		this.setQuizId(newQuiz?.quiz?.id) // Use quiz.id for API calls
		this.currentUserQuiz = newQuiz
		// Update currentQuizAnswers and currentQuizQuestions for the new quiz
		this.setCurrentQuizAnswers(newQuiz?.userQuizAnswers || [])
		this.currentQuizQuestions = newQuiz?.quiz?.quizQuestions || []
	}

	/**
	 *
	 * @param {*} newQuiz - can next quiz
	 * @param {*} currentQuiz - the current quiz
	 */
	@action handleNextQuiz = (newQuiz, currentQuiz) => {
		// Get the next quiz name via find the quiz has previousStep is the current quiz name
		const nextQuiz = this.userQuizzes.find(({ previousStep }) => previousStep === newQuiz?.quiz?.name)

		this.quizHistory = {
			...this.quizHistory,
			current: newQuiz?.quiz?.name,
			previous: currentQuiz?.quiz?.name,
			next: newQuiz?.quiz?.name === this.quizHistory.latest ? null : nextQuiz?.quiz?.name,
		}
		this.setQuizId(newQuiz?.quiz?.id) // Use quiz.id for API calls
		this.currentUserQuiz = newQuiz
		// Update currentQuizAnswers and currentQuizQuestions for the new quiz
		this.setCurrentQuizAnswers(newQuiz?.userQuizAnswers || [])
		this.currentQuizQuestions = newQuiz?.quiz?.quizQuestions || []
	}

	/**
	 * @deprecated will remove soon
	 */
	@action setAcceptTermAndPolicy = (value) => {
		this.acceptTermAndPolicy = value
	}

	@action
	setOptionsViaDataType = (dataType, options) => {
		this.selectOptionsByDataType[dataType] = options
	}

	@action
	getOptionsViaDataType = (dataType) => {
		return this.selectOptionsByDataType[dataType] || {}
	}

	/**
	 * @param {PROFILE_COMPLETION_FIELDS_NAME} dataType
	 */
	@action
	getOptionsDataViaDataType = (dataType) => {
		if (!this.selectOptionsByDataType) return []
		const { data } = this.selectOptionsByDataType[dataType] || []

		return data
	}

	@action
	setPageTitle = (title) => {
		this.pageTitle = title
	}

	@action
	setCurrentQuizQuestions = (questions) => {
		const { quiz, userQuizAnswers } = questions || {}
		this.currentQuizQuestions = quiz?.quizQuestions
		this.setCurrentQuizAnswers(userQuizAnswers)
	}

	@action
	setCurrentQuizAnswers = (answers) => {
		// Luôn set answers, kể cả khi empty array
		// Điều này đảm bảo khi switch quiz, answers được reset đúng
		if (!Array.isArray(answers)) {
			this.currentQuizAnswers = []
			this.clearAnswerIdMap()
			return
		}

		const filteredAnswers = answers.filter(({ _destroy }) => !_destroy)
		this.currentQuizAnswers = filteredAnswers

		// Sync existing answer IDs to answerIdMap when loading initial data
		this.clearAnswerIdMap()
		this.syncAnswerIdsFromResponse(filteredAnswers)
	}

	@action
	updateCurrentQuizAnswers = (answers) => {
		//TODO: Just update the answer if it has changed
		this.currentQuizAnswers = answers
	}

	/**
	 * Set the status of the profile completion.
	 * @param {any} status - The status to set.
	 */
	@action
	setStatus(status) {
		this.status = status
	}

	/**
	 * Set the user quizzes.
	 * @param {Object} quizzes - The quizzes to set.
	 */
	@action
	setUserQuizzes = (quizzes) => {
		// TODO: add more logic
		this.userQuizzes = quizzes
	}

	/**
	 * Update the parameters of the profile completion.
	 * @param {Object} params - The parameters to update.
	 */
	@action
	updateParams = (params) => {
		this.params = {
			...this.params,
			...params,
		}
	}

	/**
	 * Get the parameters as a query string.
	 * @returns {string} The parameters as a query string.
	 */
	@action
	getParamsAsQuery = () => {
		return Object.keys(this.params)
			.map((key) => `${key}=${this.params[key]}`)
			.join('&')
	}

	/**
	 * Increase the completion percentage.
	 *
	 * @deprecated will remove soon
	 */
	@action
	increasePercentage = () => {
		this.percentage += PERCENTAGE_STEP
	}

	@action
	updatePercentage = (value) => {
		this.percentage = value
	}

	/**
	 * Decrease the completion percentage.
	 */
	@action
	decreasePercentage = () => {
		this.percentage -= PERCENTAGE_STEP
	}

	/**
	 * Reset the completion percentage to 0.
	 */
	@action
	resetPercentage = () => {
		this.percentage = 0
	}

	@action
	reset = () => {
		runInAction(() => {
			this.percentage = 0
			this.params = {
				kind: null,
				percent: 0,
			}
			this.status = null
			this.previousStep = null
			this.state = null
			this.quiz = null
			this.userQuizAnswers = []
			this.id = null

			this.userQuizzes = []
			this.currentQuizQuestions = []
			this.currentQuizAnswers = []
			this.currentQuizId = null
			this.currentUserQuiz = null
			this.selectOptionsByDataType = {}
			this.acceptTermAndPolicy = false
			this.pageTitle = ''
			this.percentageStep = {}
			this.answerIdMap.clear()
			this.hasPendingAutoSave = false
			this.pendingAutoSaveCount = 0

			authStore.setRequireCompleteProfile(false)

			// Reset resume parse store as well
			resumeParseStore.reset()
		})
	}

	@action
	handleUserCompletedProfile = async (status) => {
		// Check if the user has completed the profile
		if (!status) {
			return
		}
		// 	Fetch the worker profile
		candidateStore.fetchCandidateDetail()
		// 	Set the `requireCompleteProfile` to `false`
		authStore.setRequireCompleteProfile(false)
		// 	Direct to the home page
		routerStore.replace(PATHS.care.home)
		// 	Show the dialog to notice the user that the profile completion is completed
		homeStore.updateUserJustCompletedProfile(true)
		return
	}

	/**
	 * Fetch the user data form to render the initial screening form.
	 * @returns {Array} The user quizzes.
	 */
	@action
	fetchUserForm = async () => {
		try {
			const res = await apolloClient.query({
				query: GET_USER_FORM,
				variables: {
					name: NAME,
				},
			})

			if (!res) {
				throw new Error('No response')
			}

			const { userForm } = res.data || {}
			const { userQuizzes, status } = userForm || {}
			await this.handleUserCompletedProfile(status, userQuizzes)
			const currentQuiz = getQuizObject(userQuizzes)

			userQuizzes.forEach((userQuiz) => {
				this.percentageStep[userQuiz?.quiz?.name] = userQuiz?.state === QUIZ_STATES.ANSWERED ? 100 : 0
			})

			this.setUserQuizzes(userQuizzes)
			this.setCurrentUserQuiz(currentQuiz)
			return userQuizzes
		} catch (e) {
			notifyStore.error(e?.message || "Couldn't fetch user form")
			console.error(e)
		}
	}

	/**
	 * @param {*} dataType - The question options data type to fetch the list of options
	 * @param {*} payload - The payload to fetch the list of options
	 *
	 * @returns {Array} The list of answers
	 */
	@action
	fetchQuestionOptions = async (dataType, payload = {}, loadMore = false) => {
		try {
			this.loadingOptions = true

			// Get current data for loadMore case
			const currentData = this.getOptionsViaDataType(dataType)
			const cursor = loadMore ? currentData?.rest?.pageInfo?.endCursor : null

			const res = await apolloClient.query({
				query: GET_SELECT_OPTIONS,
				variables: {
					filter: {
						dataType,
						payload,
					},
					first: 40,
					after: cursor, // Use cursor for pagination
				},
			})

			const { selectOptions } = res.data || {}
			if (!selectOptions) {
				console.error('No select options')
				return
			}

			const { nodes, pageInfo } = selectOptions || {}
			const remappedOptions = nodes.map(({ id, name }) => ({
				value: id,
				label: name,
			}))

			// Update data based on loadMore flag
			if (loadMore && currentData?.data) {
				this.setOptionsViaDataType(dataType, {
					data: [...currentData.data, ...remappedOptions],
					rest: { pageInfo }, // Store new pageInfo
				})
			} else {
				this.setOptionsViaDataType(dataType, {
					data: remappedOptions,
					rest: { pageInfo },
				})
			}

			return selectOptions
		} catch (e) {
			notifyStore.error(e?.message || "Couldn't fetch question options")
			console.error(e)
		} finally {
			this.loadingOptions = false
		}
	}

	/**
	 * The function responsible for submitting the profile completion step to the server.
	 *
	 * @param {Object} formikValues - The current Formik form values (real-time state)
	 */
	@action
	submitUpdateUserQuiz = async (formikValues) => {
		try {
			const quizId = this.currentQuizId
			if (!quizId) {
				throw new Error('No quiz ID')
			}

			// Validate form using Formik values (real-time) instead of store values (may be stale due to debounce)
			// This is critical for file uploads where autoSave hasn't completed yet
			const isFormCompleted = formikValues ? this.validateWithFormikValues(formikValues) : this.validateCurrentQuizCompletion()

			// 2nd gate to check the form is completed or not
			if (!isFormCompleted) {
				notifyStore.error('Please ensure all questions are answered before submitting.')
				return
			}

			// CHỈ gửi quizId và state, KHÔNG gửi userQuizAnswers
			// Vì submitSingleAnswer đã lưu tất cả answers rồi
			const variables = {
				quizId,
				state: QUIZ_STATES.ANSWERED,
				userQuizAnswers: [], // Empty - data đã được lưu qua submitSingleAnswer
			}

			const res = await apolloClient.mutate({
				mutation: UPDATE_USER_QUIZZES_MUTATION,
				variables: variables,
			})

			this.percentageStep[this.currentQuizName] = 100

			const { updateUserQuiz } = res.data || {}
			if (!updateUserQuiz) {
				throw new Error('No response') // TODO: change the error message to a better one
			}

			const { status, userQuizzes } = updateUserQuiz || {}
			await this.handleUserCompletedProfile(status, userQuizzes)
			const currentQuiz = getQuizObject(userQuizzes)

			this.setUserQuizzes(userQuizzes)
			this.setCurrentUserQuiz(currentQuiz)

			this.fetchUserForm()
		} catch (e) {
			void notifyStore.error(e?.message || "Couldn't submit profile completion")
			console.error(e)
		}
	}

	/**
	 * This function take responsible for find the previous/next stage in the `userForm` based on the `stateName`
	 * @param {*} quizName
	 */
	@action
	switchStage = (quizName, isBack = true) => {
		try {
			const { userQuizzes } = this
			const newQuiz = userQuizzes.find(({ quiz }, idx) => {
				if (quiz.name === quizName) {
					return userQuizzes[idx]
				}

				return null
			})

			if (!newQuiz) {
				throw new Error(`Can't find quiz with name: ${quizName}`)
			}
			if (isBack) {
				this.handleBackQuiz(newQuiz, this.currentUserQuiz)
			} else {
				// TODO: handle nextquiz here
				this.handleNextQuiz(newQuiz, this.currentUserQuiz)
			}
		} catch (e) {
			notifyStore.error(e?.message || "Couldn't switch stage")
		}
	}

	/**
	 * This function used to submit the answer on each change of the form - Auto Save
	 * Note: @action must come before @loading for proper decorator chaining
	 * */
	@action
	@loading
	submitSingleAnswer = async (answer) => {
		// NOTE: Do NOT clear hasPendingAutoSave here!
		// hasPendingAutoSave is managed by the component via setPendingAutoSave()
		// Clearing it here causes race condition when multiple fields change rapidly:
		// Field A debounce done → clear flag → Field B still in debounce → button enables incorrectly

		// Increment counter to track concurrent API calls
		this.pendingAutoSaveCount++

		// NOTE: Không update currentQuizAnswers TRƯỚC API call vì:
		// - Nó sẽ trigger useEffect trong ProfileCompletionFormContext (dependency currentQuizAnswers)
		// - Gây rebuild initialValues và re-render form NGAY LẬP TỨC
		// - Formik đã có giá trị mới từ setFieldValue rồi
		//
		// currentQuizAnswers được update SAU khi API hoàn thành (ở cuối function)
		// Lúc đó Formik values đã match với initialValues mới → không có visual flipping

		try {
			const quizId = this.currentQuizId

			if (!quizId) {
				throw new Error('No quiz ID')
			}

			// Get ID from answerIdMap first, then fallback to currentQuizAnswers
			const existingId = this.getAnswerId(answer.quizQuestionId)

			const answerWithId = {
				...answer,
				id: existingId,
			}

			const answers = useFormatValues([{ answer: answerWithId }], this.currentQuizAnswers)

			if (answers?.length <= 0) {
				return
			}

			const userQuizAnswers =
				answers?.map((item) => {
					if (item?.answer === 'AVOID_ANSWER_BLANK') {
						return {
							...item,
							answer: '',
						}
					}
					return item
				}) || []

			// Latest step is remap answer has value `AVOID_ANSWER_BLANK` to empty string
			// to avoid the issue related to BE
			const variables = {
				quizId,
				state: QUIZ_STATES.ANSWERING,
				userQuizAnswers,
			}
			const res = await apolloClient.mutate({
				mutation: UPDATE_USER_QUIZ_MUTATION,
				variables: variables,
			})
			const { updateUserQuiz } = res.data || {}

			if (!updateUserQuiz) {
				throw new Error("Can't save") // TODO: change the error message to a better one
			}

			const { userQuizzes } = updateUserQuiz || {}
			const currentQuiz = Array.from(userQuizzes).find((userQuiz) => userQuiz?.quiz?.id === quizId) || {}
			const updatedAnswers = currentQuiz?.userQuizAnswers || []

			// QUAN TRỌNG: KHÔNG update currentQuizAnswers ở đây!
			// Vì currentQuizAnswers là dependency của useEffect trong ProfileCompletionFormContext
			// Nếu update → trigger rebuild initialValues → Formik re-init → AutoDestroy chạy lại → loop vô hạn
			//
			// Thay vào đó:
			// 1. Chỉ sync ID vào answerIdMap (cho API calls sau này)
			// 2. Sync userQuizzes (cho Back/Next navigation)

			if (answer._destroy) {
				this.answerIdMap.delete(answer.quizQuestionId)
			} else {
				// Sync IDs from API response vào answerIdMap
				updatedAnswers.forEach((apiAnswer) => {
					if (apiAnswer?.id && apiAnswer?.quizQuestionId) {
						// Chỉ set nếu chưa có trong map (tránh race condition)
						if (!this.answerIdMap.has(apiAnswer.quizQuestionId)) {
							this.answerIdMap.set(apiAnswer.quizQuestionId, apiAnswer.id)
						}
					}
				})

				// CHỈ update files trong currentQuizAnswers (cần metadata từ API)
				// KHÔNG add/remove answers để tránh trigger re-render
				if (answer.files && answer.files.length > 0) {
					const submittedAnswer = updatedAnswers.find((a) => a.quizQuestionId === answer.quizQuestionId)
					if (submittedAnswer?.files && submittedAnswer.files.length > 0) {
						const index = this.currentQuizAnswers.findIndex((a) => a.quizQuestionId === answer.quizQuestionId)
						if (index !== -1) {
							// Chỉ update files, KHÔNG thay đổi array reference
							this.currentQuizAnswers[index].files = submittedAnswer.files
						}
					}
				}
			}

			// Sync userQuizzes với API response để khi Back/Next, answers vẫn đúng
			this.syncUserQuizzesAnswers(quizId, updatedAnswers)
		} catch (e) {
			void notifyStore.error(e?.message || 'Error')
			// Re-fetch user form
			this.fetchUserForm()
		} finally {
			// Decrement counter when API call completes (success or error)
			runInAction(() => {
				this.pendingAutoSaveCount = Math.max(0, this.pendingAutoSaveCount - 1)
			})
		}
	}

	/**
	 * Sync currentQuizAnswers vào userQuizzes để khi navigate Back/Next, answers vẫn đúng
	 */
	@action
	syncUserQuizzesAnswers = (quizId, answers) => {
		const quizIndex = this.userQuizzes.findIndex((uq) => uq?.quiz?.id === quizId)
		if (quizIndex !== -1) {
			this.userQuizzes[quizIndex] = {
				...this.userQuizzes[quizIndex],
				userQuizAnswers: answers,
			}
		}
	}

	@action
	removeAnswerByQuestionId = (questionId) => {
		this.currentQuizAnswers = this.currentQuizAnswers.filter((answer) => answer?.quizQuestionId !== questionId)
	}

	@action
	clearOptionsForDataType = (dataType) => {
		if (this.selectOptionsByDataType[dataType]) {
			this.selectOptionsByDataType[dataType] = null
		}
	}

	/**
	 * Get answer ID from answerIdMap or fallback to currentQuizAnswers
	 * @param {string} quizQuestionId
	 * @returns {string|null} answerId
	 */
	getAnswerId = (quizQuestionId) => {
		// First check answerIdMap (synced from API)
		if (this.answerIdMap.has(quizQuestionId)) {
			return this.answerIdMap.get(quizQuestionId)
		}
		// Fallback to currentQuizAnswers
		const existingAnswer = this.currentQuizAnswers.find((a) => a.quizQuestionId === quizQuestionId)
		return existingAnswer?.id || null
	}

	/**
	 * Sync answer IDs from API response without mutating currentQuizAnswers
	 * This prevents Formik re-initialization and UI flipping
	 * @param {Array} apiAnswers - answers from API response
	 */
	@action
	syncAnswerIdsFromResponse = (apiAnswers) => {
		if (!apiAnswers || apiAnswers.length === 0) return

		apiAnswers.forEach((apiAnswer) => {
			if (apiAnswer?.id && apiAnswer?.quizQuestionId) {
				this.answerIdMap.set(apiAnswer.quizQuestionId, apiAnswer.id)
			}
		})
	}

	/**
	 * Clear answerIdMap - called when switching quizzes or resetting
	 */
	@action
	clearAnswerIdMap = () => {
		this.answerIdMap.clear()
	}

	/**
	 * Set pending auto-save state - called immediately when field value changes
	 */
	@action
	setPendingAutoSave = (value) => {
		this.hasPendingAutoSave = value
	}
}

export const profileCompletionStore = new ProfileCompletionStore()
